import { DisplayError, Icon, Loading } from '@cotera/client/app/components';
import { ModelScope } from '@cotera/client/v0/actions/framework/context';
import { useInScopeModel } from '@cotera/client/v0/actions/framework/use-in-scope-model';
import {
  TabLink,
  useTabParams,
} from '@cotera/client/v0/tab-manager/tab-manager';
import { GraphModel } from '@cotera/client/v0/graph/graph.model';
import { Assert } from '@cotera/utilities';
import { Save, Zap } from 'lucide-react';
import { Suspense, useMemo, useRef, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useAction } from '../../actions/framework/use-action';
import { Button } from '../../components/ui/button';
import { SaveLlmColumnAction } from './actions/save-llm-column.action';
import {
  DatasetPreview,
  Model,
  Prompt,
  SystemMessage,
  Tools,
} from './components';
import { LlmColumnModel } from './llm-column.model';
import { Separator } from '../../components/ui/separator';
import { LlmModelContext, useColumnModel } from './context';
import { useWatchableValue } from '../../actions/framework';
import { Watchable } from '../../actions/framework/watchable';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
} from '../../components/ui/dropdown-menu';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '../../components/ui/resizable';
import { Text } from '@cotera/client/app/components/ui';
import { AppTab } from '../../components/tab/tab';
import { Ty } from '@cotera/era';
import { iconForGraphColumn } from '../../data-grid';
import { ATC, DG } from '@cotera/sdk/core';
import { RunStatus } from '../../components/run-status';
import { LlmRunColumnAction } from './actions/run-llm-column.action';
import { DuplicateAction } from '../../graph/duplicate.action';
import { DeleteColumnAction } from '../common-actions/delete-column.action';
import {
  RichTextEditor,
  RichTextEditorRef,
} from '@cotera/client/app/components/app/rte';
import type { EditorExtensionConfig } from '@cotera/client/app/components/app/rte/types/extension-config';
import {
  AttachmentNode,
  CoteraMention,
  Disclaimer,
  ExpressionNode,
  JsonTypeBuilderNode,
  MentionDropdownMenu,
} from '@cotera/client/app/components/ui/tiptap/extensions';
import {
  MentionData,
  useGraphMentions,
} from '@cotera/client/app/hooks/use-graph-metions';
import type { Editor as TiptapEditor } from '@tiptap/react';
import { NavigateToMentionAction } from '../../graph/navigate-to-mention.action';

// Generate a random 6-digit string
const generateRandomId = (): string => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

export const NewLlmColumn: React.FC = () => {
  const { id: dataset } = useTabParams<{
    id: string;
  }>();

  const columnName = useMemo(() => {
    return `new-column-${generateRandomId()}`;
  }, []);

  return (
    <SuspensefulColumnEditView
      dataset={dataset}
      column={columnName}
      isNew={true}
    />
  );
};

export const LlmColumnEdit: React.FC = () => {
  const { id: dataset, columnId: column } = useTabParams<{
    id: string;
    columnId: string;
  }>();

  return (
    <SuspensefulColumnEditView
      dataset={dataset}
      column={column}
      isNew={false}
    />
  );
};

const SuspensefulColumnEditView: React.FC<{
  dataset: string;
  column: string;
  isNew: boolean;
}> = ({ dataset, column, isNew }) => {
  return (
    <ErrorBoundary
      fallbackRender={(e) => (
        <DisplayError
          error={{
            message: JSON.stringify(e.error),
          }}
        />
      )}
    >
      <Suspense fallback={<Loading.Dots />}>
        <ColumnEditView dataset={dataset} column={column} isNew={isNew} />
      </Suspense>
    </ErrorBoundary>
  );
};

const ColumnEditView: React.FC<{
  dataset: string;
  column: string;
  isNew: boolean;
}> = ({ dataset, column, isNew }) => {
  const graphModel = useInScopeModel(GraphModel);

  Assert.assert(graphModel !== null);

  const model = useMemo(
    () =>
      new LlmColumnModel(
        {
          dataset,
          column,
          graph: graphModel.graph,
        },
        {
          isNew,
        }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataset, column]
  );

  return (
    <LlmModelContext.Provider value={model}>
      <AppTab model={model}>
        <ModelScope models={[model]}>
          <ResizablePanelGroup direction="horizontal" className="h-full">
            <ResizablePanel defaultSize={67} className="h-full py-4">
              <Left />
            </ResizablePanel>
            <ResizableHandle className="mx-4" />
            <ResizablePanel defaultSize={33} className="h-full py-4">
              <Right />
            </ResizablePanel>
          </ResizablePanelGroup>
        </ModelScope>
      </AppTab>
    </LlmModelContext.Provider>
  );
};

const Left: React.FC = () => {
  const model = useColumnModel();
  const version = useWatchableValue(model.version);

  switch (version) {
    case 'v1':
      return <V1Left />;
    case 'v2':
      return <V2Left />;
    default:
      return null;
  }
};

const V1Left = () => {
  return (
    <div className="flex flex-col h-full w-full divide-y divide-divider">
      <Title />
      <Model />
      <Tools />
      <div className="flex flex-col h-full">
        <ResizablePanelGroup direction="vertical" className="h-full">
          <ResizablePanel>
            <SystemMessage />
          </ResizablePanel>

          <ResizableHandle className="my-2" />

          <ResizablePanel>
            <Prompt />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  );
};

const V2Left = () => {
  const model = useColumnModel();
  const v2EditorState = useWatchableValue(model.v2EditorState);
  const graphModel = useInScopeModel(GraphModel);
  const editorRef = useRef<RichTextEditorRef>(null);
  const navigateToMentionAction = useAction(NavigateToMentionAction);

  const { options: mentionOptions, groups: mentionGroups } = useGraphMentions({
    useModel: () => ({
      graph: model.inputs.graph,
      currentNameInGraph: model.currentNameInGraph as Watchable<string | null>,
    }),
    types: ['tool', 'document', 'expr', 'column'],
    relationName: model.datasetName,
  });

  const makePlaceholder = ({
    editor,
    node,
  }: {
    editor: TiptapEditor;
    node: any;
  }): string => {
    // Check if current node is an h1 heading
    const isH1 = node.type.name === 'heading' && node.attrs['level'] === 1;

    if (isH1 && node.textContent.trim() === '') {
      return 'Give your column a name...';
    }

    // Check if there's already body text (not counting the heading)
    const doc = editor.state.doc;
    let hasBodyText = false;

    doc.descendants((node, _pos) => {
      // Skip the heading
      if (node.type.name === 'heading' && node.attrs['level'] === 1) {
        return true;
      }
      // Check if there's any text content in other nodes
      if (node.isText && node.text && node.text.trim().length > 0) {
        hasBodyText = true;
        return false; // Stop searching
      }
      return true;
    });

    // Fun placeholder variations when there's already content
    if (hasBodyText) {
      const funPlaceholders = [
        'Tell me more...',
        'What else should I know?',
        'Keep going...',
        'And then?',
        "What's next?",
        'Continue your thought...',
      ];

      // Use a simple hash of the content length to pick a consistent placeholder
      const contentLength = editor.state.doc.textContent.length;
      const index = contentLength % funPlaceholders.length;
      return funPlaceholders[index] ?? 'Tell me more...';
    }

    return "Start writing your column's prompt...";
  };

  const handleMentionClick = useCallback(
    async (data: MentionData) => {
      // Extract tool key for tool mentions
      const toolKey =
        data.t === 'tool' &&
        data.data &&
        typeof data.data === 'object' &&
        'toolId' in data.data
          ? (data.data as { toolId: string }).toolId
          : undefined;

      await navigateToMentionAction.execute({
        mentionType: data.t as 'tool' | 'document' | 'expr',
        mentionName: data.label ?? data.name,
        toolKey,
      });
    },
    [navigateToMentionAction]
  );

  const extensions = useMemo<EditorExtensionConfig>(
    () => [
      {
        extension: Disclaimer,
        config: { menus: ['turn-into', 'drag-context', 'slash'] },
      },
      {
        extension: JsonTypeBuilderNode,
        config: { menus: ['slash'] },
      },
      {
        extension: ExpressionNode.configure({
          parser: graphModel?.graph.snapshot().parser.forRel(model.datasetName),
        }),
        config: { menus: ['slash'] },
      },
      {
        extension: AttachmentNode.configure({ items: [] }),
        config: { menus: ['slash'] },
      },
      {
        extension: CoteraMention.configure({
          onClick: handleMentionClick,
        }),
      },
    ],
    [graphModel, model.datasetName, handleMentionClick]
  );

  return (
    <div className="flex flex-col h-full w-full divide-y divide-divider">
      <div className="flex-1 min-h-0 overflow-y-auto px-4">
        <RichTextEditor
          ref={editorRef}
          className="h-full"
          titlePlaceholder="Give your column a name..."
          placeholder={makePlaceholder}
          extensions={extensions}
          content={v2EditorState}
          onUpdate={(editor) => {
            const newState = editor.getJSON();
            const docState = new DG.DocumentEditor.EditorState(newState);

            model.formState.set({
              ...model.formState.snapshot(),
              name: docState.title() ?? '',
            });
            model.v2EditorState.set(newState);
          }}
        >
          <MentionDropdownMenu
            options={mentionOptions}
            groups={mentionGroups}
          />
        </RichTextEditor>
      </div>
      <Model showOutputPopover={false} />
    </div>
  );
};

const Title: React.FC = () => {
  const model = useColumnModel();
  const outputShape = useWatchableValue(model.outputShape);
  const graph = useWatchableValue(model.inputs.graph);
  const { name } = useWatchableValue(model.formState);
  const icon = iconForGraphColumn(graph, {
    rel: model.inputs.dataset,
    name: name,
  });

  return (
    <div className="pb-2 flex w-full items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon icon={icon} className="shrink-0" />
        <Text.Title
          title={name}
          type="section"
          subtitle={`llm<${Ty.displayTy(outputShape)}>`}
        />
      </div>
      <div className="flex items-center gap-2">
        <RunStatus model={model} />
      </div>
    </div>
  );
};

const Right: React.FC = () => {
  const model = useInScopeModel(LlmColumnModel);

  const runOnNewRows = useAction(LlmRunColumnAction);

  const saveAction = useAction(SaveLlmColumnAction);

  const saveEnabled = useWatchableValue(model.hasErrors);
  const duplicateColumn = useAction(DuplicateAction);
  const deleteColumn = useAction(DeleteColumnAction);
  const { rel, col } = useWatchableValue(model.identity);

  return (
    <div className="h-full flex flex-col">
      <div className="justify-end flex gap-2 shrink-0 items-center">
        <div className="flex items-center">
          <Button
            variant="secondary"
            className="rounded-r-none!"
            onClick={() => runOnNewRows.execute()}
          >
            <Zap />
            Run Column
          </Button>
        </div>
        <Button
          variant="outline"
          disabled={saveEnabled}
          onClick={() => saveAction.execute()}
        >
          <Save />
          Save
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size={'icon-sm'}>
              <Icon icon="ellipsis-vertical" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="bottom">
            {rel !== null && col !== null && (
              <TabLink
                asChild
                to={`/runs/${ATC.RunKeyHelpers.derivePrefixForColumn(
                  rel.toString(),
                  col.toString()
                )}`}
              >
                <DropdownMenuItem>
                  <Icon icon="activity" />
                  Activity
                </DropdownMenuItem>
              </TabLink>
            )}
            <DropdownMenuItem onSelect={() => deleteColumn.execute()}>
              <Icon icon="trash" />
              Delete
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => duplicateColumn.execute()}>
              <Icon icon="copy" />
              Duplicate Column
              <DropdownMenuShortcut
                shortcut={duplicateColumn.action?.shortcut}
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Separator className="my-4" />
      <DatasetPreview />
    </div>
  );
};
